/*
 * Copyright (C) 2005 - 2023. Cloud Software Group, Inc. All Rights Reserved. Confidential & Proprietary.
 * Licensed pursuant to commercial Cloud Software Group, Inc. End User License Agreement.
 */

import { PieChartSeriesOptions } from 'highcharts';
import datasetTypes from '../../enums/datasetTypes';
import PieSeriesGenericChartVisualization from './chart/PieSeriesGenericChartVisualization';
import genericChartVisualizationStaticPropertiesReducer from './chart/reducers/genericChartVisualizationReducer';
import { computePiePositionsAndSizes } from './chart/util/PiePlotUtils';

import {
    getFieldToLabelMeasureMapping,
    getTooltipValue,
    getValue,
    pieSeriesVisitor
} from './chart/util/GenericSeriesUtil';
import visitMultiAxisDatasetNode from '../../store/util/visitMultiAxisDatasetNode';
import { getEvents, getOutputParamFromMeasure, getOutputParamsKeyFromField } from './chart/util/EventUtil';
import i18n from '../../../i18n/AdHocBundle.properties';
import { ILinkOutputParams } from './types/commonTypes';

function getSerieTitleYOffset(amountOfLines :number) {

    const titleTextLineHeight = 15;
    const textTopBottomPadding = 10;
    const titleHeight = titleTextLineHeight * amountOfLines + textTopBottomPadding;

    // see issue JRS-18649 for reasons why we have this offset
    // in short: after HC upgrade all labels moved a little bit up, and
    // we have to bring them down
    const textTopExtraOffset = 3;

    return -titleHeight + textTopExtraOffset;
}

export class PieVisualization extends PieSeriesGenericChartVisualization {

    getChartSection() {
        const chart = super.getChartSection();

        return {
            ...chart,
            type: 'pie'
        };
    }

    getPlotOptionsSection(): any {

        const section = super.getPlotOptionsSection();

        const headerFormat = '{point.point.jrsTooltipHeader}';
        const pointFormat = '<span style="color:{point.color}">\u25CF</span> {point.jrsTooltipMeasureName}<b>{point.jrsTooltipValue}</b>';

        return {
            ...section,
            pie: {
                ...section.pie,
                tooltip: {
                    headerFormat,
                    pointFormat
                }
            }
        }
    }

    getSeriesSection(selectedColIndex: number, selectedRowIndex: number): PieChartSeriesOptions[] {
        const dataset = this.getDataset();
        const linkOptions = this.getLinkOptions();
        const levels = this.getStaticProperties().levelsStructure;
        const containerWidth = this.getContainerWidth();
        const containerHeight = this.getContainerHeight();
        const includeMeasureName = this.showMeasureName();

        // console.log('==================================');

        const { aggregations } = dataset.data;

        const measuresLevelFromColumns = levels.columns.find((level: any) => (level.kind === 'measures'));
        const measuresLevelFromRows = levels.rows.find((level: any) => (level.kind === 'measures'));
        const measuresLevel = measuresLevelFromColumns || measuresLevelFromRows;
        const measureFieldToLabelMapping = getFieldToLabelMeasureMapping(levels);
        const measureFieldToIndexMap :any = {};
        measuresLevel.measures.forEach((measure :any, measureIndex :number) => {
            measureFieldToIndexMap[measure.field] = measureIndex;
        });

        const haveEvents :boolean = linkOptions && Object.keys(linkOptions.events).length > 0;

        const rowItems :any[] = dataset.summary.rows.items;
        const columnItems :any[] = dataset.summary.columns.items;
        const amountOfMeasures :number = measuresLevel.measures.length;
        let measurePosition = 0;
        rowItems.forEach((item :any, index :number) => {
            if (item.measuresLevel) {
                measurePosition = index;
            }
        });
        columnItems.forEach((item :any, index :number) => {
            if (item.measuresLevel) {
                measurePosition = index;
            }
        });
        const measuresOnColumns :boolean = !!(columnItems.find((item) => item.measuresLevel));
        const measuresOnRows = !measuresOnColumns;

        let columnSeries = visitMultiAxisDatasetNode(dataset.data.columns, pieSeriesVisitor, {
            result: [],
            stack: [],
            levels: levels.columns,
            measureFieldToLabelMapping,
            measureFieldToIndexMap,
            includeMeasureName,
            selectedIndex: selectedColIndex,
            skipTotals: dataset.summary.columns.items.length === selectedColIndex
        }).result;
        // console.log('columnSeries:', JSON.parse(JSON.stringify(columnSeries)));

        let rowSeries = visitMultiAxisDatasetNode(dataset.data.rows, pieSeriesVisitor, {
            result: [],
            stack: [],
            levels: levels.rows,
            measureFieldToLabelMapping,
            measureFieldToIndexMap,
            includeMeasureName,
            selectedIndex: selectedRowIndex,
            skipTotals: dataset.summary.rows.items.length === selectedRowIndex
        }).result;
        // console.log('rowSeries:', JSON.parse(JSON.stringify(rowSeries)));

        if (measuresLevelFromColumns && selectedColIndex <= measurePosition) {
            const newColumnSeries = [];
            for (let i = 0; i < columnSeries.length; i += 1) {
                for (let m = 0; m < amountOfMeasures; m += 1) {
                    newColumnSeries.push({
                        ...columnSeries[i],
                        index: columnSeries[i].indexes[m],
                        measureIndex: m
                    });
                }
            }
            columnSeries = newColumnSeries;
            // console.log('extended columnSeries:', JSON.parse(JSON.stringify(columnSeries)));
        }
        if (measuresLevelFromRows && selectedRowIndex <= measurePosition) {
            const newRowSeries = [];
            for (let i = 0; i < rowSeries.length; i += 1) {
                for (let k = 0; k < amountOfMeasures; k += 1) {
                    newRowSeries.push({
                        ...rowSeries[i],
                        measureIndex: k
                    });
                }
            }
            rowSeries = newRowSeries;
            // console.log('extended rowSeries:', JSON.parse(JSON.stringify(rowSeries)));
        }

        const defaultSerieOptions = {
            type: 'pie',
            dataLabels: {
                enabled: false
            },
            point: {
                events: getEvents(linkOptions)
            },
            title: {
                verticalAlign: 'top'
            }
        }

        const getTooltipValueProps = {
            measures: measuresLevel.measures,
            defaultFieldAggregationType: this.defaultFieldAggregationType,
            defaultFieldAggregationFormat: this.defaultFieldAggregationFormat
        };

        let firstMinMaxRecorded :boolean = false,
            min :number = 0,
            max :number = 0;
        const pies: any[] = [];

        let showPieSlicesInLegend = (measuresOnRows && 1 < amountOfMeasures) || 0 < selectedRowIndex;

        for (let c = 0; c < columnSeries.length; c += 1) {

            const columnSerie = columnSeries[c];

            let measureFromColumn :any = null;
            if (measuresOnColumns) {
                measureFromColumn = measuresLevel.measures[columnSerie.measureIndex];
            }

            let pieTitle :any = [i18n['adhoc.node.total.node']];
            if (includeMeasureName && measureFromColumn) {
                pieTitle = [measureFromColumn.label];
            }
            if (0 < selectedColIndex) {
                pieTitle = [...columnSerie.pathByFields];
                if (includeMeasureName && measureFromColumn) {
                    pieTitle = [...columnSerie.pathByFieldsAndMeasures];
                    if (selectedColIndex <= measurePosition) {
                        // we need to display measure label but since the variable
                        // 'pathByFieldsAndMeasures' doesn't have the measure label in it (because the Column slider
                        // it not positioned beyond the measure), we have to add measure label into pie title
                        pieTitle.push(measureFromColumn.label);
                    }
                }
            }
            const pieTitleYOffset :number = getSerieTitleYOffset(pieTitle.length);
            pieTitle = pieTitle.join('<br/>');

            const outputParamsFromColumns: ILinkOutputParams = {
                measures: [],
                values: []
            };
            if (haveEvents) {
                if (measureFromColumn) {
                    outputParamsFromColumns.measures.push(getOutputParamFromMeasure(measureFromColumn));
                }

                for (let i = 0; i < selectedColIndex; i += 1) {
                    if (columnItems[i].measuresLevel) {
                        // we skip measure items
                        continue;
                    }
                    outputParamsFromColumns.values.push({
                        key: getOutputParamsKeyFromField(columnItems[i]),
                        value: columnSerie.pathByFieldsAndMeasures[i]
                    });
                }
            }

            const slices: any = [];
            for (let r = 0; r < rowSeries.length; r += 1) {
                const rowSerie = rowSeries[r];

                let measureFromRow :any = null;
                if (measuresOnRows) {
                    measureFromRow = measuresLevel.measures[rowSerie.measureIndex];
                }

                let measure = measureFromColumn;
                if (measureFromRow) {
                    measure = measureFromRow;
                }
                let tooltipMeasureName = '';
                if (includeMeasureName) {
                    tooltipMeasureName = `${measure.label}: `;
                }

                let value = 0;
                if (measureFromColumn) {
                    value = getValue(aggregations, rowSerie.index, columnSerie.index);
                } else {
                    /* eslint-disable-next-line */
                    if (0 < selectedColIndex) {
                        value = getValue(aggregations, rowSerie.index + rowSerie.measureIndex, columnSerie.index);
                    } else {
                        value = getValue(aggregations, rowSerie.index + rowSerie.measureIndex, dataset.data.aggregations[0].length - 1);
                    }
                }
                const tooltipValue = getTooltipValue(getTooltipValueProps, value, null, measure.id);
                if (!firstMinMaxRecorded) {
                    min = value;
                    max = value;
                    firstMinMaxRecorded = true;
                } else {
                    min = Math.min(min, value);
                    max = Math.max(max, value);
                }

                let sliceNameInLegend :any = [];
                let sliceTooltipHeader = '';
                if (0 < selectedRowIndex) {
                    sliceNameInLegend = this.getDrilldownEnabled() ? rowSerie.pathByFields.slice(-1) : [...rowSerie.pathByFields];
                    sliceTooltipHeader = this.getDrilldownEnabled() ? rowSerie.pathByFields.slice(-1)[0] : rowSerie.pathByFields.join(', ');
                }
                // and we add measure name to the legend section
                if (includeMeasureName && measureFromRow) {
                    sliceNameInLegend.push(measure.label);
                }
                sliceNameInLegend = sliceNameInLegend.join(', ');

                if (tooltipMeasureName === '' && sliceTooltipHeader !== '') {
                    // move the field values into bottom row to show something before the dot
                    tooltipMeasureName = `${sliceTooltipHeader}: `;
                    sliceTooltipHeader = '';
                }
                if (sliceTooltipHeader) {
                    // add the new line break if header is not empty
                    sliceTooltipHeader += '<br/>';
                }

                const linkOutputParams: ILinkOutputParams = {
                    measures: [],
                    values: []
                };
                if (haveEvents) {
                    const outputParamsFromRows: ILinkOutputParams = {
                        measures: [],
                        values: []
                    };

                    if (measureFromRow) {
                        outputParamsFromRows.measures.push(getOutputParamFromMeasure(measureFromRow));
                    }

                    for (let i = 0; i < selectedRowIndex; i += 1) {
                        if (rowItems[i].measuresLevel) {
                            // we skip measure items
                            continue;
                        }
                        outputParamsFromRows.values.push({
                            key: getOutputParamsKeyFromField(rowItems[i]),
                            value: rowSerie.pathByFieldsAndMeasures[i]
                        });
                    }

                    linkOutputParams.measures = [...outputParamsFromColumns.measures, ...outputParamsFromRows.measures];
                    linkOutputParams.values = [...outputParamsFromColumns.values, ...outputParamsFromRows.values];
                }

                slices.push({
                    y: value,
                    name: sliceNameInLegend,
                    jrsTooltipHeader: sliceTooltipHeader,
                    jrsTooltipMeasureName: tooltipMeasureName,
                    jrsTooltipValue: tooltipValue,
                    linkOutputParams
                });
            }

            pies.push({
                ...defaultSerieOptions,
                name: 'pieName',
                data: [...slices],
                showInLegend: showPieSlicesInLegend,
                title: {
                    ...defaultSerieOptions.title,
                    text: pieTitle,
                    y: pieTitleYOffset
                }
            });

            // since all pies have / should have the same set of slices, it makes sense to show slices only
            // from first pie
            showPieSlicesInLegend = false;
        }

        this.recordMinMaxValues(min, max);

        const centerAndSize: { center: any[], size: string }[] = computePiePositionsAndSizes(pies, containerWidth, containerHeight);

        for (let i = 0; i < pies.length; i += 1) {
            const pie = pies[i];
            pie.center = centerAndSize[i].center;
            pie.size = centerAndSize[i].size;
        }

        // console.log('pies:', JSON.parse(JSON.stringify(pies)));

        return pies;
    }
}

export const PieVisualizationDescriptor = {
    legacyAdhoc: 'pie',
    cssClass: 'Pie',
    bundleName: 'adhoc.visualisation.chooser.type.PIE',
    name: 'Pie',
    group: 'adhoc.visualisation.chooser.type.GROUP_PIE',
    datasetType: datasetTypes.MULTI_AXIS,
    component: PieVisualization,
    staticPropertiesReducer: genericChartVisualizationStaticPropertiesReducer,
    supportsResize: false,
    requirements: {
        measures: { min: 1 },
        fields: { min: 0 }
    }
};
